import { Maybe, Nullish } from '../types';

export const areAllTruthy = (arr: unknown[]): boolean =>
  arr.every((val) => val);

export const areAllFalsy = (arr: unknown[]): boolean =>
  arr.every((val) => !val);

export const isAnyFalsy = (arr: unknown[]): boolean => !areAllTruthy(arr);

export const isAnyTruthy = (arr: unknown[]): boolean => !areAllFalsy(arr);

export const areAllEqual = (arr: unknown[], condition: unknown): boolean =>
  arr.every((val) => val === condition);

export const isAnyNonEqual = (arr: unknown[], condition: unknown): boolean =>
  !areAllEqual(arr, condition);

export const isDate = (val: unknown): val is Date => val instanceof Date;

export const isNumber = (val: unknown): val is number =>
  typeof val === 'number';

export const isNumOrBigInt = (val: unknown): val is number | bigint =>
  ['number', 'bigint'].includes(typeof val);

export const isObject = (val: unknown): val is Record<string, unknown> =>
  val !== null && !Array.isArray(val) && typeof val === 'object';

export const isArray = <T>(val: T | T[]): val is T[] => Array.isArray(val);

export const isNullish = (val: unknown) => val === undefined || val === null;

export const isNonNullish = <T extends Maybe<unknown>>(
  val: unknown,
): val is Exclude<T, Nullish> => !isNullish(val);

export const assertNullish = (val: unknown): val is Nullish => isNullish(val);

type ForceNull<T, G = Nullish> = T extends G ? null : T;

/** If argument is nullish (`undefined` or `null`), return `null`. Otherwise return argument. */
export const forceNull = <T>(val: T): ForceNull<T> => {
  if (assertNullish(val)) {
    return null as ForceNull<T>;
  }
  return val as ForceNull<T>;
};

export const throwOnNullish = <T>(arg: T): Exclude<T, Nullish> => {
  if (isNullish(arg)) {
    throw new TypeError('Nullish value supplied');
  }
  return arg as Exclude<T, Nullish>;
};
